import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react"
import React, { useEffect, useMemo, useRef } from "react"
import { PageLoader } from "./page-loader"
import { useSelector } from "react-redux"
import { useNavigate, useLocation } from "react-router-dom"
import { usePermissions } from "../../hooks/queries/usePermissions"
import useFetchUser from "../../hooks/useFetchUser"
import useLogging from "../../hooks/useLogging"

export const AuthenticationGuard = ({ component, allowedPermissions, sidebarOpen }) => {
	const { user, isAuthenticated } = useAuth0()
	const { data: permissionsPayload } = usePermissions()
	const permissions = permissionsPayload?.permissions ?? null
	const termsAccepted = useFetchUser()
	const logging = useLogging()
	const navigate = useNavigate()
	const location = useLocation()

	// App passes `component` as a fresh inline arrow on every render (it closes
	// over sidebarOpen), so wrapping it directly recreated the HOC each render
	// and REMOUNTED the whole page on every sidebar toggle. Keep the latest
	// render function + sidebarOpen in refs and wrap a stable inner component
	// ONCE, so toggling re-renders the page instead of remounting it.
	const latestComponent = useRef(component)
	latestComponent.current = component
	const sidebarOpenRef = useRef(sidebarOpen)
	sidebarOpenRef.current = sidebarOpen

	const Component = useMemo(
		() =>
			withAuthenticationRequired(() => latestComponent.current(), {
				onRedirecting: () => (
					<div>
						<div className={`${sidebarOpenRef.current ? "pl-64" : "pl-0"}`}>
							<div className="page-layout">
								<PageLoader />
							</div>
						</div>
					</div>
				),
				loginOptions: {
					appState: { returnTo: location.pathname }
				}
			}),
		// Wrap once; the refs keep the rendered content + loader padding current.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	)

	// Check if the user has any of the allowed permissions
	const hasPermission = allowedPermissions.some(
		permission => permissions && permissions.includes(permission)
	)

	const isScheduleLocked = useSelector(state => state.schedule.isLocked)

	useEffect(() => {
		if (
			location.pathname === "/ic-tracker" &&
			!isScheduleLocked &&
			permissions &&
			permissions.includes("read:schedules")
		) {
			navigate("/home")
		} else if (permissions && !hasPermission && location.pathname !== "/error") {
			if (!isAuthenticated) {
				navigate("/error")
			} else {
				console.log("You are authenticated")
			}
		} else if (
			user &&
			(termsAccepted === false ||
				termsAccepted === undefined ||
				(termsAccepted && termsAccepted.length === 0)) &&
			location.pathname !== "/terms-and-conditions"
		) {
			logging.info("going to terms and conditions")
			navigate("/terms-and-conditions")
		}
		// `logging` is a fresh object each render (useLogging returns inline object); adding it would re-run every render.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		hasPermission,
		navigate,
		termsAccepted,
		user,
		isAuthenticated,
		isScheduleLocked,
		permissions,
		location.pathname
	])

	if (!user) {
		return null
	}

	return <Component />
}
